/**
 * Potansiyel müşteriler (v0.1.90): the shapes of GET /leads/prospects and
 * GET /leads/prospects/{leadId}, and the Turkish labels the page shows. Instants are epoch
 * milliseconds, money is micro USD, like the rest of the panel view model.
 */

import type { Instant } from './types.js';

export type ProspectKind =
  | 'canopy_tray'
  | 'fabrication'
  | 'accessories_4x4'
  | 'trailer'
  | 'auto_repair'
  | 'marine'
  | 'directory_media'
  | 'retail_other'
  | 'unknown';

export type Production = 'yes' | 'likely' | 'possible' | 'unlikely' | 'no' | 'unknown';

export interface ProspectDecisionSummary {
  verdict: string;
  verdictLabel: string;
  at: Instant | null;
  by: string | null;
  gate: string | null;
  reasons: string[];
  count: number;
}

export interface ProspectRow {
  leadId: string;
  companyId: string;
  name: string;
  legalName: string | null;
  domain: string | null;
  website: string | null;
  domainCheck: string | null;
  role: string | null;
  /** The gate's role verdict in Turkish (v0.1.95). */
  roleLabel?: string | null;
  registry: string | null;
  registryId: string | null;
  country: string | null;
  region: string | null;
  locality: string | null;
  state: string | null;
  /** site: from the company's address; scan: the scanned region, a candidate only (v0.1.95). */
  stateBasis?: 'site' | 'registry' | 'scan' | 'name' | null;
  /** v0.1.106: the sources or the postcode disagree on the state. */
  locationConflict?: boolean;
  postcode: string | null;
  address: string | null;
  lat: number | null;
  lon: number | null;
  stage: string;
  stageChangedAt: Instant | null;
  fit: { vehicle: number; market: number; design: number; size: number; gap: number; total: number };
  confidence: number | null;
  kind: ProspectKind;
  kindLabel: string;
  kindBasis: 'pilot' | 'guess' | 'gate';
  kindMatched: string | null;
  production: Production;
  productionLabel: string;
  productionBasis: 'pilot' | 'guess' | 'gate';
  why: string;
  makes: string | null;
  tier: string | null;
  source: string;
  sourceRef: string | null;
  providers: string[];
  hits: number;
  found: {
    at: Instant | null;
    provider: string | null;
    query: string | null;
    locality: string | null;
    cellId: string | null;
  };
  firstSeenAt: Instant | null;
  lastActivityAt: Instant | null;
  decision: ProspectDecisionSummary | null;
  unfitReason: string | null;
  /** v0.1.106: why a lead waits (a target short of a condition, a channel partner). */
  stageNote?: string | null;
  contact: {
    emails: number;
    phones: number;
    /** v0.1.110: the business e-mail addresses found on the company's site, and the contact page. */
    emailList?: string | null;
    contactPage?: string | null;
    sent: number;
    /** v0.1.118: drafts waiting for Sercan's review. */
    drafts?: number;
    lastSentAt: Instant | null;
    lastContactAt: Instant | null;
    lastReplyAt: Instant | null;
    manualOnly: boolean;
  };
}

export interface ProspectJobRun {
  kind: string;
  startedAt: Instant | null;
  heartbeatAt: Instant | null;
  selected: number;
  processed: number;
}

export interface ProspectJobRecent {
  kind: string;
  status: string;
  stopReason: string | null;
  startedAt: Instant | null;
  finishedAt: Instant | null;
  selected: number;
  processed: number;
  succeeded: number;
  errors: number;
  note: string | null;
}

/** v0.1.124: an estimate of how many firms there are, from web search and the lists together. */
export interface ProspectEstimate {
  /** Firms of this kind found so far, by any source. */
  found: number;
  /** Of them, found by web search, on a trade show or directory list, and both ways. */
  web: number;
  listed: number;
  both: number;
  /** Chapman's estimate (a floor), or null while the two ways share fewer than seven firms. */
  estimate: number | null;
}

/** v0.1.124: map coverage, where the firms came from, and how many there are likely to be. */
export interface ProspectReach {
  cells: {
    scanned: number;
    total: number;
    /** makerQueries: the state-wide maker queries run in the region (v0.1.126; absent from an older API). */
    byRegion: { region: string; name: string; scanned: number; total: number; makerQueries?: number }[];
  };
  origins: { key: string; firms: number; qualified: number }[];
  estimate: { firms: ProspectEstimate; qualified: ProspectEstimate };
}

export const ORIGIN_LABELS_TR: Readonly<Record<string, string>> = {
  cells: 'Harita hücreleri',
  maker: 'Eyalet çapında üretici araması',
  trade_show: 'Fuar katılımcı listeleri',
  directory: 'Sektör rehberleri',
  other: 'Diğer (deneme dönemi)',
};

export interface ProspectsView {
  asOf: Instant;
  summary: {
    total: number;
    byStage: Record<string, number>;
    byKind: Record<string, number>;
    byProduction: Record<string, number>;
    byCountry: Record<string, number>;
    bySource: Record<string, number>;
    qualified: number;
    verified: number;
    decided: number;
    foundToday: number;
    decidedToday: number;
    withEmail: number;
    mailed: number;
    replied: number;
  };
  costs: {
    scanTotalMicroUsd: number;
    scanTodayMicroUsd: number;
    verifyTotalMicroUsd: number;
    verifyTodayMicroUsd: number;
  };
  jobs: {
    running: ProspectJobRun[];
    next: { kind: string; at: Instant | null; what: string }[];
    recent: ProspectJobRecent[];
    cells: { scanned: number; total: number; week: number; quota: number };
    queue: { waiting: number; due: number };
  };
  /** v0.1.124; absent from an older API. */
  reach?: ProspectReach;
  rows: ProspectRow[];
}

export interface ProspectDetail {
  asOf: Instant;
  row: ProspectRow;
  found: {
    at: Instant | null;
    provider: string;
    query: string;
    family: string | null;
    template: string | null;
    locality: string | null;
    cellId: string | null;
    rank: number | null;
    url: string | null;
    filter: string | null;
    resolution: string | null;
  }[];
  evidence: {
    id: string;
    kind: string;
    claim: string;
    polarity: string;
    url: string | null;
    domain: string | null;
    at: Instant | null;
    group: string | null;
    phrase: string | null;
    query: string | null;
    title: string | null;
    snippet: string | null;
  }[];
  decisions: {
    id: string;
    at: Instant | null;
    stage: string;
    verdict: string;
    verdictLabel: string;
    by: string | null;
    modelFamily: string | null;
    confidence: number | null;
    applied: boolean;
    rule: string | null;
    evidence: number;
    reasons: { code: string; labelTr: string; detail: string }[];
  }[];
  history: { from: string | null; to: string; at: Instant | null; actor: string; reason: string | null }[];
  channels: {
    type: string;
    value: string | null;
    status: string | null;
    mxOk: boolean | null;
    verifiedAt: Instant | null;
    url: string | null;
    noUnsolicited: boolean;
  }[];
  messages: {
    id: string;
    kind: string | null;
    channel: string | null;
    to: string | null;
    subject: string | null;
    status: string | null;
    scheduledFor: Instant | null;
    sentAt: Instant | null;
    step: number | null;
    body: string | null;
    /** v0.1.118: the Turkish reading of a draft, the delivery mode, why it is blocked and its campaign. */
    subjectTr?: string | null;
    bodyTr?: string | null;
    deliveryMode?: string | null;
    blockReason?: string | null;
    campaign?: string | null;
  }[];
  /** v0.1.127: the outreach lines (partnership and scans, both CadGaragePro's since v0.1.170), why not, and which mail goes first. */
  plan?: {
    arm: string;
    partnerReason: string | null;
    scanReason: string | null;
    offer: { name: string; listingUrl: string | null }[];
  } | null;
}

/** v0.1.127: which mail goes first (packages/leads outreach/plan.ts). */
export const OUTREACH_ARM_TR: Readonly<Record<string, string>> = {
  partner_first: 'Önce iş birliği maili, sonra CAD dosyaları maili',
  scan_first: 'Önce CAD dosyaları maili, sonra iş birliği maili',
  partner_only: 'Yalnız iş birliği maili',
  scan_only: 'Yalnız CAD dosyaları maili',
  none: 'Bu firmaya mail hazırlanmadı',
};

/** v0.1.127: why a company is outside an outreach line (packages/leads OUTREACH_REASON_TR). */
export const OUTREACH_REASON_TR: Readonly<Record<string, string>> = {
  unfit: 'uygun değil aşamasında',
  not_qualified: 'nitelikli değil',
  not_target_role: 'üretici, imalatçı, montajcı ya da bayi değil',
  competitor: 'rakip (tarama ya da CAD satıyor)',
  service: 'hizmet firması (ayar, servis, söküm)',
  no_makes: '"Ne yapıyor" boş: kişiye özel mail yazılamaz',
  no_vehicle: 'ürettiği ürünlerde araç adı yok',
  camper_trailer: 'kamp römorku ya da karavan üreticisi',
  no_offer: 'önerecek aracımız yok (hepsine zaten ürün yapıyor)',
  no_channel: 'iletişim bilgisi yok',
  country: 'ülkesi doğrulanmadı ya da ülke kuralı yok',
  no_relevance: 'neden potansiyel olduğu yazılmadı (gönderim kapısı bunu ister)',
  not_off_road: 'off-road işi yaptığı doğrulanmadı (sitesi okunmadı ya da başka bir iş)',
  line_paused: 'iş birliği hattı durduruldu (2 Kasım 2026: satış tarama teklifine odaklandı)',
};

/** Where a kind or production value comes from, as the page says it. */
export function basisText(basis: 'pilot' | 'guess' | 'gate', matched?: string | null): string {
  if (basis === 'pilot') return 'pilot notundan';
  if (basis === 'gate') return 'kapının rol kararından';
  return matched ? `adından tahmin ("${matched}")` : 'adından tahmin';
}

export const STAGE_LABELS_TR: Readonly<Record<string, string>> = {
  bulundu: 'Bulundu',
  nitelikli: 'Nitelikli',
  dogrulandi: 'Doğrulandı',
  temas: 'Temas',
  cevap: 'Cevap',
  musteri: 'Müşteri',
  uygun_degil: 'Uygun değil',
};

export const STAGE_ORDER = ['bulundu', 'nitelikli', 'dogrulandi', 'temas', 'cevap', 'musteri', 'uygun_degil'] as const;

export const KIND_LABELS_TR: Readonly<Record<ProspectKind, string>> = {
  canopy_tray: 'Kanopi, tepsi, ute gövdesi',
  fabrication: 'Metal ve fabrikasyon atölyesi',
  accessories_4x4: '4x4 aksesuar ve montaj',
  trailer: 'Römork ve karavan',
  auto_repair: 'Oto servis ve yedek parça',
  marine: 'Tekne ve denizcilik',
  directory_media: 'Dizin, ilan sitesi, medya',
  retail_other: 'İlgisiz işletme',
  unknown: 'Tür belli değil',
};

export const KIND_ORDER: readonly ProspectKind[] = [
  'canopy_tray',
  'fabrication',
  'accessories_4x4',
  'trailer',
  'auto_repair',
  'marine',
  'unknown',
  'directory_media',
  'retail_other',
];

export const PRODUCTION_LABELS_TR: Readonly<Record<Production, string>> = {
  yes: 'Üretiyor',
  likely: 'Muhtemelen üretiyor',
  possible: 'Üretiyor olabilir',
  unlikely: 'Muhtemelen üretmiyor',
  no: 'Üretmiyor',
  unknown: 'Bilinmiyor',
};

export const PRODUCTION_ORDER: readonly Production[] = ['yes', 'likely', 'possible', 'unknown', 'unlikely', 'no'];

export const PROVIDER_LABELS_TR: Readonly<Record<string, string>> = {
  brave: 'Brave web araması',
  serper: 'Google web araması',
  places: 'Google Places',
  here: 'HERE haritası',
  overpass: 'OpenStreetMap',
  gemini: 'Genesis Eye araması',
  web_search: 'Web araması',
  osm: 'OpenStreetMap',
  directory: 'Dizin (pilot)',
  manual: 'Elle',
};

export const COUNTRY_LABELS_TR: Readonly<Record<string, string>> = {
  AU: 'Avustralya',
  NZ: 'Yeni Zelanda',
  US: 'ABD',
  CA: 'Kanada',
  GB: 'Birleşik Krallık',
  ZA: 'Güney Afrika',
};

export const DECIDER_LABELS_TR: Readonly<Record<string, string>> = {
  rule: 'kapı (kural)',
  agent: 'Doğrulayıcı',
  human: 'Sercan',
  referee: 'hakem',
};

export const JOB_STATUS_TR: Readonly<Record<string, string>> = {
  completed: 'bitti',
  partial: 'yarım',
  failed: 'hata',
  aborted: 'durduruldu',
  running: 'çalışıyor',
};

/** Why a kind is (or is not) worth our time, the same lines the API uses for `why`. */
export const KIND_WHY_TR: Readonly<Record<ProspectKind, string>> = {
  canopy_tray: 'Araca özel kanopi ve tepsi üretiyor; aracın 3D taraması ölçü almayı ve tasarımı hızlandırır.',
  fabrication: 'Metal üretim yapıyor; tampon, koruma, raf gibi araca özel parçaları taramayla tasarlayabilir.',
  accessories_4x4: '4x4 aksesuar satıyor ya da takıyor; kendi ürününü geliştiriyorsa tarama verisi işine yarar.',
  trailer: 'Römork ve karavan yapıyor; çeken aracın ölçüleri ve bağlantı noktaları için tarama kullanabilir.',
  auto_repair: 'Oto servis; kendi parçasını tasarlamıyorsa hedef dışı olabilir, incelenmeli.',
  marine: 'Tekne işi; araç taramasıyla bağı zayıf.',
  directory_media: 'Bir firma değil, dizin ya da ilan sitesi; hedef dışı.',
  retail_other: 'Araç ekipmanıyla ilgisi görünmüyor; hedef dışı.',
  unknown: 'Adından ve kaynaklardan ne yaptığı çıkmadı; doğrulama kapısı sitesini okuyunca belli olur.',
};

/** growth.leads.unfit_reason in Turkish. */
export const UNFIT_LABELS_TR: Readonly<Record<string, string>> = {
  competitor: 'rakip',
  not_target_type: 'hedef tür değil',
  closed: 'kapalı',
  country_blocked: 'ülke kapalı',
  no_unsolicited_notice: 'istenmeyen ileti kabul etmiyor',
  suppressed: 'durdurma listesinde',
  duplicate: 'kopya',
  no_channel: 'kanal yok',
  sercan_decision: 'Sercan kararı',
  other: 'diğer',
};

/** growth.companies.domain_check in Turkish. */
export const DOMAIN_CHECK_LABELS_TR: Readonly<Record<string, string>> = {
  unchecked: 'site kontrol edilmedi',
  owned: 'site firmanın',
  rejected: 'site firmanın değil',
  parked: 'park edilmiş alan adı',
  uncertain: 'site sahipliği belirsiz',
};

export const CHANNEL_LABELS_TR: Readonly<Record<string, string>> = {
  generic_email: 'Genel e-posta',
  role_email: 'Görev e-postası',
  form: 'İletişim formu',
  phone_business: 'İş telefonu',
  whatsapp_business: 'WhatsApp Business',
};

export const CHANNEL_STATUS_TR: Readonly<Record<string, string>> = {
  candidate: 'aday',
  usable: 'kullanılabilir',
  manual_only: 'sadece elle',
  blocked: 'engelli',
  bounced: 'geri döndü',
  suppressed: 'durdurma listesinde',
  retired: 'emekli',
};

export const BLOCK_REASON_TR: Readonly<Record<string, string>> = {
  no_unsolicited_notice: 'sitede "istenmeyen mesaj göndermeyin" notu var',
  suppressed: 'gönderilmeyecekler listesinde',
  channel_not_usable: 'adres kullanılabilir değil',
  domain_mismatch: 'adres firmanın alan adında değil',
  sending_disabled: 'gönderim kapalı',
  replied: 'firma cevap verdi',
};

export const DELIVERY_MODE_TR: Readonly<Record<string, string>> = {
  gmail_api: 'e-posta',
  manual_form: 'iletişim formu (elle)',
  manual_phone: 'telefon (elle)',
  manual_whatsapp: 'WhatsApp (elle)',
};

export const MESSAGE_STATUS_TR: Readonly<Record<string, string>> = {
  draft: 'taslak',
  awaiting_approval: 'onay bekliyor',
  approved: 'onaylandı',
  scheduled: 'zamanlandı',
  sending: 'gönderiliyor',
  sent: 'gönderildi',
  bounced_hard: 'geri döndü (kalıcı)',
  bounced_soft: 'geri döndü (geçici)',
  failed: 'gönderilemedi',
  cancelled: 'iptal',
  blocked: 'engellendi',
  manual_pending: 'elle gönderilecek',
};

/** growth.evidence.claim in Turkish: what a piece of evidence speaks to. */
export const CLAIM_LABELS_TR: Readonly<Record<string, string>> = {
  identity: 'Kimlik',
  activity: 'Faaliyet',
  role: 'Ne iş yaptığı',
  channel: 'İletişim kanalı',
  vehicle_fit: 'Araç uyumu',
  size: 'Büyüklük',
  legal_form: 'Şirket türü',
  location: 'Konum',
  no_unsolicited: 'İstenmeyen ileti notu',
};

/** growth.query_results.filter_result and resolution in Turkish. */
export const HIT_FILTER_TR: Readonly<Record<string, string>> = {
  pending: 'bekliyor',
  kept: 'tutuldu',
  blocked_domain: 'engelli alan adı',
  marketplace: 'pazar yeri',
  social: 'sosyal medya',
  directory: 'dizin',
  duplicate: 'kopya',
  not_target: 'hedef değil',
  location_trap: 'yanlış konum',
};

export const HIT_RESOLUTION_TR: Readonly<Record<string, string>> = {
  pending: 'eşleştirme bekliyor',
  new_company: 'yeni firma olarak eklendi',
  existing_company: 'var olan firmaya bağlandı',
  rejected: 'reddedildi',
};
